import type { WorkflowDefinition } from "../types";

/**
 * W12 — Crisis communications.
 *
 * `perTarget` on a tag: an incident is not the property, and two incidents can
 * be live at once. The tag is asked for at start (default `crisis`), keys the
 * run, and feeds the `promptTag` predicate through `run.context.tag`, so a
 * second start with the same tag resumes the existing run rather than forking
 * a parallel one.
 *
 * `since` predicates stay property-wide here: a tag has no event slice of its
 * own, and annotating the incident is a property-timeline act.
 */
export const crisisResponseWorkflow: WorkflowDefinition = {
	id: "crisis-response",
	wNumber: 12,
	title: "Handle crisis communications when AI is repeating the story",
	outcome:
		"Track whether AI repeats the story, which sources feed it, and whether your response lands.",
	audiences: ["pr", "inhouse"],
	uses: [
		"Key Messages",
		"Prompts",
		"Projects",
		"Annotations",
		"Citation Intelligence",
		"Coverage Groups",
	],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "tag", label: "Incident tag", defaultTag: "crisis" },
	isApplicable: (mode) => mode.showProjects && mode.showKeyMessages,
	articleSlug:
		"how-to-run-crisis-communications-when-ai-is-repeating-the-story",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "capture-messages",
			title: "Write down the claims and your answer to them",
			page: "Key Messages",
			route: "/key-messages",
			criterion:
				"You've added the harmful claims and your counter-messages since you started.",
			why: "The harmful claims go in as negative messages and your response as positive ones. A paraphrase still counts as a match.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Add each harmful claim as a negative key message",
				"Add your counter-story as positive key messages",
				"Tag both sets with the incident tag",
			],
			// An incident is a dated event: the claims and the answer to them are
			// written for *this* crisis, so the standing message set on the
			// property must not check the step off.
			complete: {
				kind: "all",
				of: [
					{ kind: "since", event: "negativeKeyMessageAdded" },
					{ kind: "since", event: "positiveKeyMessageAdded" },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.keyMessages.negative} harmful claims captured`,
									done:
										state.property.keyMessages.negative > 0,
								},
								{
									label: `${state.property.keyMessages.positive} counter-messages captured`,
									done:
										state.property.keyMessages.positive > 0,
								},
							],
						}
					: null,
		},
		{
			id: "crisis-prompts",
			title: "Add the questions people are asking",
			page: "Prompts",
			route: "/discovery-queries",
			criterion: "You've tagged prompts for this incident.",
			why: "You need the questions a worried customer, journalist or investor would really ask. The tag lets every dashboard filter to the incident.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Write prompts about the story and the brand's safety record",
				"Add the should-I-still-buy questions",
				"Tag them all with the incident tag",
			],
			complete: { kind: "promptTag", tag: "crisis" },
			signal: ({ state, run }) => {
				if (!state.property) {
					return null;
				}
				const tag = run?.context.tag ?? "crisis";
				return {
					kind: "checklist",
					items: [
						{
							label: `Prompts tagged "${tag}"`,
							done: state.property.promptTags.includes(tag),
						},
					],
				};
			},
		},
		{
			id: "crisis-project",
			title: "Start tracking those prompts daily",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've created a project since you started.",
			why: "A project puts the crisis prompts on a daily schedule from day one. You get a daily read instead of waiting.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a General project named for the incident",
				"Add the tagged prompts to it",
			],
			// The incident gets its own project; a project the account was already
			// running is not a daily read on this crisis.
			complete: { kind: "since", event: "projectCreated" },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
		{
			id: "alerts",
			title: "Check who gets negative message alerts",
			page: "Settings",
			route: "/settings?tab=notifications#negative-message-alerts",
			criterion: "At least one person gets these alerts.",
			why: "A crisis moves faster than a dashboard check. Your comms leads should hear about it in their inbox.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Check that the comms leads are on the negative message alerts list",
				"Add agency addresses if they should get them too",
			],
			requires: "negativeMessageAlerts",
			complete: {
				kind: "count",
				key: "negativeMessageAlertRecipientCount",
				gte: 1,
			},
		},
		{
			id: "annotate",
			title: "Note every statement you put out",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted a statement or story since you started.",
			why: "Statements, corrections and major stories show up on every trend chart. When the story moves, you can point at what moved it.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Note the first story and your first statement",
				"Keep noting as coverage develops",
			],
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Events noted",
							value: state.property.manualAnnotationCount,
						}
					: null,
		},
		{
			id: "monitor-narrative",
			title: "Watch the story day by day",
			page: "Visibility",
			route: "/dashboard/visibility",
			criterion: "You've got daily data for the tagged prompts.",
			why: "Watch two things: how often the claims surface, and whether your answer is landing. The spike is expected, so the question is how fast it fades.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Filter the visibility drill-down to the incident tag",
				"Check the claims against your notes daily",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "messageTrend",
			// The real series comes from the message-tracking hook in the step
			// panel; the loader deliberately carries no time series.
			signal: () => null,
		},
		{
			id: "trace-sources",
			title: "Find the sources feeding the story",
			page: "Messages",
			route: "/dashboard/messages",
			criterion: "You've identified the sources behind the story.",
			why: "AI answers cite their sources. Knowing which outlets carry the story tells you where a correction actually changes AI answers.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Open the matched answers for the claim you are watching",
				"Read the Sources tab for the outlets cited in those answers",
				"Chase corrections at the most-cited outlets first",
			],
			requires: "messageMatches",
			// Manual on purpose: nothing watches "you read the source list".
			complete: { kind: "manual" },
		},
		{
			id: "corrective-coverage",
			title: "Track your corrective coverage",
			page: "Coverage Groups",
			route: "/coverage-groups",
			criterion: "You've created a coverage group since you started.",
			why: "Group the corrective stories and your own statements. Then you can see whether AI is starting to cite your side.",
			docsHref: "/docs/dashboards/coverage-groups",
			tasks: [
				"Import the corrective coverage URLs as a group",
				"Score the placements",
				"Watch the group's AI Citations funnel",
			],
			// Corrective coverage is imported for this incident. An older group
			// on the property — last quarter's launch, say — is not it.
			complete: { kind: "since", event: "coverageGroupCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.coverageGroupCount} coverage groups`,
									done: state.property.coverageGroupCount > 0,
								},
								{
									label: "Placements scored",
									done:
										state.property
											.coverageGroupsWithScoredPlacements >
										0,
								},
							],
						}
					: null,
		},
		{
			id: "stand-down",
			title: "Close it out and debrief",
			page: "Projects",
			route: "/projects",
			criterion:
				"You've completed and exported the project since you started.",
			why: "Completing the project freezes the record. The export is your debrief on how far the story spread.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Mark the project Complete once things return to normal",
				"Export the PDF for the retrospective",
			],
			complete: {
				kind: "all",
				of: [
					{ kind: "since", event: "projectCompleted" },
					{ kind: "since", event: "projectExported" },
				],
			},
		},
	],
};
